import { useRouteLoaderData } from 'react-router';
import { type loader as rootLoader } from '#app/root.tsx';

function isUser(
  user: any,
): user is Awaited<ReturnType<typeof rootLoader>>['data']['user'] {
  return user && typeof user === 'object' && typeof user.id === 'string';
}

export function useOptionalUser() {
  const data = useRouteLoaderData<typeof rootLoader>('root');
  if (!data || !isUser(data.user)) {
    return undefined;
  }
  return data.user;
}

export function useUser() {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    throw new Error(
      'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
    );
  }
  return maybeUser;
}

type Action =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'list'
  | 'write'
  | 'view'
  | 'administrate'
  | 'moderate'
  | 'settings'
  | 'assign-permissions'
  | 'assign-roles'
  | 'manage'
  | 'target-roles';
type Entity =
  | 'user'
  | 'admin'
  | 'kbase'
  | 'cbmetatag'
  | 'cbquiz'
  | 'dashboard'
  | 'ops';
type Access = 'own' | 'any' | 'assigned' | 'public' | 'own,any' | 'any,own';
export type PermissionString =
  | `${Entity}:${Action}`
  | `${Entity}:${Action}:${Access}`;

export function parsePermissionString(permissionString: PermissionString) {
  const [entity, action, access] = permissionString.split(':') as [
    Entity,
    Action,
    Access | undefined,
  ];
  return {
    action,
    entity,
    access: access ? (access.split(',') as Array<Access>) : undefined,
  };
}

export function userHasPermission(
  user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined,
  permission: PermissionString,
) {
  if (!user) return false;

  // Check if user has admin role first
  const isAdmin = userHasRole(user, 'admin');
  if (isAdmin) return true;

  const { action, entity, access } = parsePermissionString(permission);
  return user.roles.some((role) =>
    role.permissions.some(
      (permission) =>
        permission.entity === entity &&
        permission.action === action &&
        (!access || access.includes(permission.access)),
    ),
  );
}

export function userHasRole(
  user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
  role: string,
) {
  if (!user) return false;
  return user.roles.some((r) => r.name === role);
}

export function getUserDisplayName(
  user: {
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
    suffix?: string | null;
    nickname?: string | null;
    username?: string;
  } | null | undefined
): string {
  if (!user) return 'Unknown User';

  // Prefer nickname if available
  if (user.nickname) return user.nickname;

  // Build full name from parts
  const parts = [
    user.firstName,
    user.middleName,
    user.lastName,
    user.suffix,
  ].filter(Boolean);

  if (parts.length > 0) return parts.join(' ');

  // Fallback to username
  return user.username || 'Unknown User';
}

export function getUserShortName(
  user: {
    firstName?: string | null;
    lastName?: string | null;
    nickname?: string | null;
    username?: string;
    profile?: {
      preferredName?: string | null;
    } | null;
  } | null | undefined
): string {
  if (!user) return 'Unknown';

  // Prefer preferredName from profile
  if (user.profile?.preferredName) return user.profile.preferredName;

  // Then nickname from user table
  if (user.nickname) return user.nickname;

  // Use first name or first + last initial
  if (user.firstName) {
    if (user.lastName) {
      return `${user.firstName} ${user.lastName.charAt(0)}.`;
    }
    return user.firstName;
  }

  return user.username || 'Unknown';
}

export function getUserFullName(
  user: {
    firstName?: string | null;
    lastName?: string | null;
    username?: string;
  } | null | undefined
): string {
  if (!user) return 'N/A';

  // Build full name: firstName lastName
  const parts = [
    user.firstName,
    user.lastName,
  ].filter(Boolean);

  if (parts.length > 0) return parts.join(' ');

  // Fallback to username
  return user.username || 'N/A';
}
